// @flow
import React, { Fragment } from 'react';

import Link from 'app/common/components/Link';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import {
  BASE_DOC_URL_VB,
  NEW_EXPERTS_BASE_URL,
} from 'app/developer-v3/constants';
import {
  getUserFields,
  messageField,
} from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

import { commonStyles } from './commonStyles';

export const createSellZapier = (isLoggedIn: boolean) => ({
  value: 'sell-zapier',
  label: 'I want to resell on behalf of Zapier or be an affiliate',
  zendeskCustomFieldValue: 'pcf-partnerships-platform-resell',
  child: help({
    docs: {
      title: 'Zapier Reseller',
      body: () => (
        <Fragment>
          <p>Thank you for your interest in partnering with Zapier!</p>
          <p>
            As of now, we do not have a reseller or affiliate program in place.
          </p>
          <p>
            We do, however, have an implementation program focused on helping
            users with Zapier. If this is of interest to your business, please
            fill out{' '}
            <Link href={`${NEW_EXPERTS_BASE_URL}/new-experts/`} target="_blank">
              this form
            </Link>
            . If you have any questions about our channel partnership options
            please contact experts@zapier.com.
          </p>
        </Fragment>
      ),
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what additional questions you have.`,
      },
    ],
  }),
});

export const createHireBuildFullIntegration = (isLoggedIn: boolean) => ({
  value: 'hire-full-integration',
  label: 'I want to hire a developer to build a full integration for me',
  zendeskCustomFieldValue: 'pcf-partnerships-platform-hire-full-integration',
  child: help({
    docs: {
      title: 'Zapier Trusted Developers',
      body: () => (
        <Fragment>
          <p>
            Zapier Trusted Developers help our Partners build on Zapier. These
            are individuals/groups who have demonstrated their capabilities by
            satisfying our requirements for building apps on our Developer
            Platform, and we recommend as a substitute for building your
            integration in-house.
          </p>
        </Fragment>
      ),
      links: [
        {
          href: `${BASE_DOC_URL_VB}/quickstart/trusted-developers`,
          title: 'Trusted Developers',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what questions you have about hiring a Zapier Trusted Developer.`,
      },
    ],
  }),
});

export const createBuildIntegrations = (isLoggedIn: boolean) => ({
  value: 'build-integrations',
  label: 'I want to build integrations on behalf of software providers',
  zendeskCustomFieldValue: 'pcf-partnerships-platform-build-integrations',
  child: help({
    docs: {
      title: 'Certified Developer Program',
      body: () => (
        <Fragment>
          <p>
            Thank you for your interest! Although our Certified Developer
            Program is not currently seeking new applicants, our Experts program
            is likely a great place for your business to start. This program is
            focused on training and certifying businesses like yours on how to
            best help Zapier users succeed with our platform. Please fill out{' '}
            <Link
              href={`${NEW_EXPERTS_BASE_URL}/l/new-experts/`}
              target="_blank"
            >
              this form
            </Link>{' '}
            so we can contact you with next steps as they become available!
          </p>
        </Fragment>
      ),
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what additional questions you have.`,
      },
    ],
  }),
});

export const createPartnerProgramAndBenefits = (isLoggedIn: boolean) => ({
  value: 'partner-program',
  label: 'How the Zapier Partner Program works',
  zendeskCustomFieldValue: 'pcf-partnerships-platform-partner-program',
  child: help({
    docs: {
      title: 'How the Zapier Partner Program works',
      body: () => (
        <Fragment>
          <p>
            The Zapier Integration Partner Program (accessible to public
            platform partners) and all of its benefits are free. All you have to
            do is get more users and maintain a healthy integration in order to
            access benefits!
          </p>
          <ul style={commonStyles.ul}>
            <li>
              We’ll share personalized recommendations for growing your
              integration, based on what’s worked for our top partners.
            </li>
            <li>
              Our team acts as frontline support for your integration, triaging
              bugs and passing along your users’ feedback.
            </li>
            <li>
              As your integration grows, you’ll gain more benefits from the
              program.
            </li>
          </ul>
        </Fragment>
      ),
      links: [
        {
          href: `${ZAPIER_APP_BASE_URL}/platform/partner-program`,
          title: 'Partner Program Overview',
        },
        {
          href: `${BASE_DOC_URL_VB}/publish/partner-program`,
          title: 'About the Partner Program',
        },
        {
          href: `${BASE_DOC_URL_VB}/publish/partner-faq`,
          title: 'Zapier Partner Program tips',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what questions you have about the Partner program and benefits.`,
      },
    ],
  }),
});

export const createBuildWorkflows = (isLoggedIn: boolean) => ({
  value: 'build-workflows',
  label:
    "I want to build workflows on behalf of users/interested in Zapier's Experts program",
  zendeskCustomFieldValue: 'pcf-partnerships-platform-experts',
  child: help({
    docs: {
      title: 'Zapier Experts Program',
      body: () => (
        <Fragment>
          <p>Thank you for your interest in becoming a Zapier Expert!</p>
          <p>Please visit the link below to apply:</p>
          <ul>
            <li>
              <Link
                href={`${NEW_EXPERTS_BASE_URL}/l/new-experts/`}
                target="_blank"
              >
                Become a Zapier Expert
              </Link>
            </li>
          </ul>
          <p>
            After applying, we highly recommend checking out{' '}
            <Link href="https://learn.zapier.com/" target="_blank">
              Zapier Learn
            </Link>{' '}
            for skills training and reading about the work our existing Experts
            are doing with users by visiting the links below.
          </p>
        </Fragment>
      ),
      links: [
        {
          href: `https://learn.zapier.com/`,
          title: 'Zapier Learn',
        },
        {
          href: `${ZAPIER_APP_BASE_URL}/experts`,
          title: 'Zapier Experts',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what questions you have about the Zapier experts program or bulding workflows for other users.`,
      },
    ],
  }),
});

export const createHireExpert = (isLoggedIn: boolean) => ({
  value: 'hire-expert',
  label: 'I want to hire someone to build Zaps for me',
  zendeskCustomFieldValue: 'pcf-partnerships-platform-hire-expert',
  child: help({
    docs: {
      title: 'Zapier Experts Program',
      body: () => (
        <Fragment>
          <p>
            Our Experts team is happy to help find someone to build Zaps for
            you.
          </p>
          <p>
            Please share a few project details by completing the form linked
            below:
          </p>
          <ul>
            <li>
              <Link
                href={`${ZAPIER_APP_BASE_URL}/experts/matchmaking`}
                target="_blank"
              >
                Zapier Experts - Matchmaking
              </Link>
            </li>
          </ul>
          <p>
            Once submitted, our team will match you with the right Expert for
            your project. If you’d prefer, please feel free to browse our{' '}
            <Link href={`${ZAPIER_APP_BASE_URL}/l/experts`} target="_blank">
              directory of Experts
            </Link>
            .
          </p>
        </Fragment>
      ),
      links: [
        {
          href: `${ZAPIER_APP_BASE_URL}/experts/matchmaking`,
          title: 'Zapier Experts - Matchmaking',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know what questions you have about hiring a Zapier expert.`,
      },
    ],
  }),
});
